import React, {useEffect, useState} from "react";
import {ConfigProvider, Modal} from "antd";
import {Trans} from "@lingui/react/macro"
import {getDeviseName, getDeviseSymbol} from "../../../i18n";
import {getLayoutDirection} from "../../../locales";
import {getPrimaryColor} from "../tokens";

/**
 * Choix de la devise à l'arrivée sur une campagne multi-devises.
 *
 * Option de campagne : config.showDeviseModal (onglet « Devises » de l'admin).
 * La fenêtre s'ouvre une fois par session et par campagne ; la devise choisie est
 * mémorisée en sessionStorage puis présélectionnée dans la modale de don (modal-don.js).
 * Contrairement à components/MultiDevise.js (site classique), aucune redirection :
 * la campagne gère toutes ses devises sur la même page.
 */

const CHANGE_EVENT="campagne-devise-change"

const storageKey=(campagneId)=>"campagne_devise_"+campagneId

// Devises proposées : principale (implicite) + devises de la campagne dont le taux est connu.
// Source unique partagée avec le sélecteur de la modale de don.
export function getCampagneCurrencyOptions(context,campagneData){
    const principalDevise=(campagneData?.principalDevise || context?.org?.devise || "eur").toLowerCase()
    const campCurrencies=(Array.isArray(campagneData?.currencies)?campagneData.currencies:[]).filter(c=>c && c.devise && !c.rateUnavailable)
    return [{devise:principalDevise,type:"PRINCIPAL",rate:1},...campCurrencies]
}

// Prix d'un produit dans une devise :
// - principale : montant tel quel ;
// - devise RÉELLE avec override prices[devise] : prix exact saisi par l'admin ;
// - sinon (réelle sans override, ou virtuelle) : conversion live montant * rate.
//
// ⚠️ Le prix fixe n'a de sens que pour une devise réellement encaissée. Sur une devise
// virtuelle, le donateur paie en devise principale : garder un ancien prix fixe (posé
// quand la devise était réelle) affichait un montant qui n'était pas celui débité.
export {getProductPrice} from "./product-price"

export function readCampagneDevise(campagneId){
    if(typeof window==="undefined") return null
    try{
        return window.sessionStorage.getItem(storageKey(campagneId))
    }catch(e){
        return null
    }
}

export function storeCampagneDevise(campagneId,devise){
    try{
        window.sessionStorage.setItem(storageKey(campagneId),devise)
    }catch(e){}
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT,{detail:{campagneId,devise}}))
}

// Abonnement au choix (la modale de don est déjà montée quand le donateur choisit).
export function onCampagneDeviseChange(campagneId,callback){
    const listener=(e)=>{
        if(e.detail?.campagneId===campagneId) callback(e.detail.devise)
    }
    window.addEventListener(CHANGE_EVENT,listener)
    return ()=>window.removeEventListener(CHANGE_EVENT,listener)
}

const getFlagUrl=(country)=>{
    if(!country) return null
    const code=country.toLowerCase()==="uk" ? "gb" : country.toLowerCase()
    return "https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/4x3/"+code+".svg"
}

export default function DeviseChoiceModal({context,campagneData})
{
    const [open,setOpen]=useState(false)

    const options=getCampagneCurrencyOptions(context,campagneData)
    const principalDevise=options[0].devise
    const enabled=!!campagneData?.config?.showDeviseModal && options.length>1

    useEffect(()=>{
        const requested=context.query?.devise
        const validLinkCurrency=typeof requested==="string" && options.some(c=>c.devise===requested.toLowerCase())
        if(enabled && !validLinkCurrency && !readCampagneDevise(campagneData.id)){
            setOpen(true)
        }
    },[enabled,campagneData?.id])

    if(!enabled){
        return null
    }

    const choose=(devise)=>{
        storeCampagneDevise(campagneData.id,devise)
        setOpen(false)
    }

    const primaryColor=getPrimaryColor(campagneData.config)

    return (
        <ConfigProvider direction={getLayoutDirection(context.langValue)} theme={{token:{colorPrimary:primaryColor}}}>
            <Modal
                open={open}
                onCancel={()=>choose(principalDevise)}
                footer={null}
                centered
                width={440}
                maskClosable={false}
                styles={{body:{padding:0},content:{borderRadius:18,overflow:"hidden",padding:0}}}
            >
                <style dangerouslySetInnerHTML={{__html:styles}}/>
                <div className="cdc" style={{"--cdc-primary":primaryColor}}>
                    <div className="cdc__title">
                        <Trans>Sélectionnez votre devise</Trans>
                    </div>
                    <div className="cdc__list">
                        {options.map(c=>{
                            const country=c.type==="PRINCIPAL" ? campagneData?.mainAssocInfo?.country : c.country
                            const symbol=getDeviseSymbol(context,c.devise)
                            const name=c.label || getDeviseName(context,c.devise) || c.devise.toUpperCase()
                            return (
                                <button key={c.devise} type="button" className="cdc__item" onClick={()=>choose(c.devise)}>
                                    <span className="cdc__flag">
                                        {country
                                            ? <img src={getFlagUrl(country)} alt=""/>
                                            : <span className="cdc__symbol">{symbol}</span>}
                                    </span>
                                    <span className="cdc__info">
                                        <span className="cdc__name">{name}</span>
                                        <span className="cdc__code">{c.devise.toUpperCase()}{symbol ? " ("+symbol+")" : ""}</span>
                                    </span>
                                    <span className="cdc__arrow">→</span>
                                </button>
                            )
                        })}
                    </div>
                </div>
            </Modal>
        </ConfigProvider>
    )
}

const styles=`
.cdc { padding: 28px 24px 24px; }
.cdc__title { font-size: 20px; font-weight: 800; color: #1a1a2e; text-align: center; margin-bottom: 18px; }
.cdc__list { display: flex; flex-direction: column; gap: 10px; }
.cdc__item {
  display: flex; align-items: center; gap: 14px;
  width: 100%; padding: 14px; text-align: start; cursor: pointer;
  background: #fff; border: 1.5px solid #e5eaf0; border-radius: 14px;
  transition: transform .12s ease, border-color .12s ease, box-shadow .12s ease;
}
.cdc__item:hover { transform: translateY(-1px); border-color: var(--cdc-primary); box-shadow: 0 6px 14px rgba(0,0,0,.08); }
.cdc__flag {
  flex-shrink: 0; width: 48px; height: 36px; border-radius: 6px; overflow: hidden;
  background: #f3f4f6; box-shadow: 0 0 0 1px rgba(0,0,0,.06);
  display: inline-flex; align-items: center; justify-content: center;
}
.cdc__flag img { width: 100%; height: 100%; object-fit: cover; display: block; }
.cdc__symbol { font-weight: 800; font-size: 20px; color: var(--cdc-primary); }
.cdc__info { flex: 1 1 auto; display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.cdc__name { font-size: 15px; font-weight: 700; color: #1a1a2e; }
.cdc__code { font-size: 12px; color: #6b7280; }
.cdc__arrow { flex-shrink: 0; font-size: 18px; color: var(--cdc-primary); opacity: .55; }
[dir="rtl"] .cdc__arrow { transform: scaleX(-1); }
`
